/**
 * Admin Types for Product Management
 * Version: 1.0.0
 */

// Product type enum
export type ProductType = 'geogrid' | 'drainage';

// ============= Geogrid Product Types =============

/**
 * Geogrid product response from admin API
 */
export interface IAdminGeogridProduct {
    id: string;
    prodotto: string;
    family: string;
    familyId: string;
    category: 'GEOGRID' | 'GEOTEXTILE';
    tensileMax: number | null;
    tensile: number | null;
    tensile1Percent: number | null;
    tensile2Percent: number | null;
    tensile3Percent: number | null;
    tensile4Percent: number | null;
    tensile5Percent: number | null;
    torsionalStiffness: number | null;
    applicableProjects: string[] | null;
    applicableRegions: string[] | null;
    // LCR / Calculation fields
    lcrEquationM2: number | null;
    lcrEquationM1: number | null;
    lcrEquationC: number | null;
    lcrEquationThreshold: number | null;
    lcrBase: number | null;
    lcrSubbase: number | null;
    grade: number | null;
    drainageCoefficientOverride: number | null;
    databaseReference: string | null;
}

/**
 * Request DTO for creating/updating geogrid products
 */
export interface IAdminGeogridProductRequest {
    prodotto: string;
    family: string;
    familyId: string;
    category: 'GEOGRID' | 'GEOTEXTILE';
    tensileMax?: number | null;
    tensile?: number | null;
    tensile1Percent?: number | null;
    tensile2Percent?: number | null;
    tensile3Percent?: number | null;
    tensile4Percent?: number | null;
    tensile5Percent?: number | null;
    torsionalStiffness?: number | null;
    applicableProjects?: string[] | null;
    applicableRegions?: string[] | null;
    // LCR / Calculation fields
    lcrEquationM2?: number | null;
    lcrEquationM1?: number | null;
    lcrEquationC?: number | null;
    lcrEquationThreshold?: number | null;
    lcrBase?: number | null;
    lcrSubbase?: number | null;
    grade?: number | null;
    drainageCoefficientOverride?: number | null;
    databaseReference?: string | null;
}

// ============= Drainage Product Types =============

/**
 * Drainage product response from admin API
 */
export interface IAdminDrainageProduct {
    id: string;
    displayName: string;
    category: string;
    effectivePorosity: number | null;
    permeabilityCoefficient: number | null;
    transmissivity: number | null;
    thickness: number | null;
    rfCreep: number | null;
    description: string | null;
    databaseReference: string | null;
    notes: string | null;
    applicableLayersOnly: string[] | null;
}

/**
 * Request DTO for creating/updating drainage products
 */
export interface IAdminDrainageProductRequest {
    displayName: string;
    category: string;
    effectivePorosity?: number | null;
    permeabilityCoefficient?: number | null;
    transmissivity?: number | null;
    thickness?: number | null;
    rfCreep?: number | null;
    description?: string | null;
    databaseReference?: string | null;
    notes?: string | null;
    applicableLayersOnly?: string[] | null;
}

// ============= API Response Types =============

/**
 * Response for geogrid products list
 */
export interface IGeogridProductsResponse {
    type: 'geogrid';
    count: number;
    products: IAdminGeogridProduct[];
}

/**
 * Response for drainage products list
 */
export interface IDrainageProductsResponse {
    type: 'drainage';
    count: number;
    products: IAdminDrainageProduct[];
}

/**
 * Generic API error response
 */
export interface IApiErrorResponse {
    message: string;
    error?: string;
}

// ============= UI State Types =============

/**
 * Admin products state for the hook
 */
export interface IAdminProductsState {
    geogridProducts: IAdminGeogridProduct[];
    drainageProducts: IAdminDrainageProduct[];
    loading: boolean;
    error: string | null;
    selectedProductType: ProductType;
    geogridFamilies: string[];
    drainageCategories: string[];
}

/**
 * DataGrid column definition
 */
export interface IDataGridColumn<T> {
    key: keyof T;
    label: string;
    editable: boolean;
    type: 'text' | 'number' | 'select' | 'multiselect';
    options?: string[];
    width?: number | string;
    minWidth?: number;
}

/**
 * Filter state for products
 */
export interface IProductFilters {
    family?: string;
    category?: string;
    projectType?: string;
    search?: string;
}

// ============= Validation Types =============

/**
 * Validation error for a field
 */
export interface IFieldValidationError {
    field: string;
    message: string;
}

/**
 * Result of product validation
 */
export interface IValidationResult {
    isValid: boolean;
    errors: IFieldValidationError[];
}

// ============= Project Types for Geogrid =============

export const APPLICABLE_PROJECT_TYPES = ['airport', 'railways', 'paved', 'unpaved', 'container_yard', 'parking_deck'] as const;

export type ApplicableProjectType = (typeof APPLICABLE_PROJECT_TYPES)[number];

// ============= Drainage Categories =============

export const DRAINAGE_CATEGORIES = ['macdrainW', 'reinforcement', 'composite'] as const;

export type DrainageCategoryType = (typeof DRAINAGE_CATEGORIES)[number];

// ============= Applicable Layers for Drainage =============

export const APPLICABLE_LAYERS = ['base', 'subbase'] as const;

export type ApplicableLayerType = (typeof APPLICABLE_LAYERS)[number];
